/**
 * @module DataTableFunctionBar
 * This module provides the primary toolbar for the DataTable, containing search,
 * state filters, and header actions.
 */

import React from 'react';
import {
  Button,
  ButtonText,
  ButtonIcon,
  HStack,
  Spinner,
  Box,
  Heading,
  Tooltip,
  TooltipContent,
  TooltipText,
  Icon,
  Pressable,
} from '@gluestack-ui/themed';
import { ListChecks } from 'lucide-react-native';
import SearchWithFilters from '@/admin/common/SearchWithFilters';
import BulkActionsOverlay from './BulkActionsOverlay';
import StateFilterMenu from './StateFilterMenu';
import type {
  ActionConfig, FilterConfig, SearchConfig, SelectionConfig,
} from '../types';
import { ActionConfig as SingleActionConfig } from '../actionTypes';

interface HeaderActionsProps {
  actions: [string, SingleActionConfig][];
  onAction?: (actionCode: string, data?: unknown, callbacks?: { onClearSelection: () => void; onExitSelectionMode: () => void }) => void;
  isActionRunning?: boolean;
}

/** Header action buttons (right side) */
const HeaderActions: React.FC<HeaderActionsProps> = ({ actions, onAction, isActionRunning }) => (
  <HStack space="sm">
    {actions?.map(([actionCode, action]) => {
      const isPrimary = actionCode === 'create' || action.action === 'primary';
      return (
        <Button
          key={actionCode}
          action={isPrimary ? 'primary' : 'secondary'}
          variant={action.variant || 'solid'}
          size="sm"
          onPress={() => onAction?.(actionCode)}
          testID={`action-button-${actionCode}`}
          isDisabled={isActionRunning}
          borderRadius="$lg"
        >
          {!!action.icon && <ButtonIcon as={action.icon} mr="$2" />}
          <ButtonText>{action.label}</ButtonText>
        </Button>
      );
    })}
  </HStack>
);

interface DataTableFunctionBarProps {
  tableTitle?: string | null;
  noTitle?: boolean;
  objectName: string;
  isLoading?: boolean;
  searchConfig: SearchConfig;
  filterConfig: FilterConfig;
  actionConfig: ActionConfig;
  selectionConfig: SelectionConfig;
}

/**
 * DataTableFunctionBar - The main functional header for the DataTable.
 *
 * This component acts as a container for:
 * - The table title (dynamic or based on object name).
 * - A powerful search bar (`SearchWithFilters`) with autocomplete.
 * - A state-based filter menu (`StateFilterMenu`).
 * - Global header actions (e.g., Create, Export).
 * - Multi-selection mode toggle.
 * - Loading and action-running indicators.
 *
 * It uses the "Grouped Config Objects" pattern to manage its sub-component dependencies cleanly.
 *
 * @param {DataTableFunctionBarProps} props - Grouped configuration for search, filters, actions, and selection.
 * @returns {React.ReactElement} The rendered toolbar.
 */
const DataTableFunctionBar: React.FC<DataTableFunctionBarProps> = ({
  tableTitle = null,
  noTitle = false,
  objectName,
  isLoading,
  searchConfig,
  filterConfig,
  actionConfig,
  selectionConfig,
}) => {
  const {
    onSearch,
    showSearch = true,
    searchPlaceholder = 'Search...',
    searchableFields = [],
    defaultSearchField = 'title',
    searchMaxWidth = 600,
  } = searchConfig;

  const {
    showStateFilter = true,
    lifecycleConfig = {},
    selectedStates = [],
    onStatesChange,
    includeDeprecated = false,
    onIncludeDeprecatedChange,
    hideFilterLabel = false,
  } = filterConfig;

  const { actions, onAction, isActionRunning = false } = actionConfig;

  const {
    selectedRows = [],
    bulkActions,
    onClearSelection,
    isSelectionMode = false,
    onToggleSelectionMode,
    enableSelection = false,
  } = selectionConfig;

  return (
    <HStack sx={{ justifyContent: 'space-between' } as unknown as React.ComponentProps<typeof HStack>['sx']} testID="data-table-function-bar" position="relative">
      <BulkActionsOverlay
        selectedRows={selectedRows}
        bulkActions={bulkActions}
        objectName={objectName}
        onAction={onAction}
        onClearSelection={onClearSelection}
        isActionRunning={isActionRunning}
      />

      <HStack alignItems="center" flex={1}>
        {!!tableTitle && !noTitle && (
          <Heading size="md" color="$primary" mr="$6">
            {tableTitle}
          </Heading>
        )}

        <HStack alignItems="center" space="md" flex={1}>
          {!!showSearch && !!onSearch && (
            <Box flex={1} maxWidth={searchMaxWidth}>
              <SearchWithFilters
                testID="search-input"
                onSearch={onSearch}
                searchPlaceholder={searchPlaceholder}
                isDisabled={isActionRunning}
                searchableFields={searchableFields}
                defaultSearchField={defaultSearchField}
              />
            </Box>
          )}

          {(isLoading || isActionRunning) && <Spinner ml="$2" size="small" />}

          {!!showStateFilter && (
            <StateFilterMenu
              lifecycleConfig={lifecycleConfig}
              selectedStates={selectedStates}
              onStatesChange={onStatesChange}
              includeDeprecated={includeDeprecated}
              onIncludeDeprecatedChange={onIncludeDeprecatedChange}
              isActionRunning={isActionRunning}
              hideLabel={hideFilterLabel}
            />
          )}

          {!!enableSelection && (bulkActions?.length ?? 0) > 0 && (
            <Tooltip
              placement="bottom"
              trigger={(triggerProps) => (
                <Pressable
                  {...triggerProps}
                  onPress={() => onToggleSelectionMode(!isSelectionMode)}
                  disabled={isActionRunning}
                  p="$2"
                  ml="$1"
                  borderRadius="$md"
                  bg={isSelectionMode ? '$bgBrandSubtle' : 'transparent'}
                  testID="selection-mode-toggle"
                >
                  <Icon as={ListChecks} size="sm" color={isSelectionMode ? '$brandBase' : '$textSubtle'} />
                </Pressable>
              )}
            >
              <TooltipContent>
                <TooltipText>{isSelectionMode ? 'Exit selection mode' : 'Select multiple items'}</TooltipText>
              </TooltipContent>
            </Tooltip>
          )}
        </HStack>
      </HStack>

      <HeaderActions actions={actions} onAction={onAction} isActionRunning={isActionRunning} />
    </HStack>
  );
};

export default DataTableFunctionBar;
